import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

export default function Notices() {
  const [notices, setNotices] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", type: "offer", member_id: "", member_name: "", borough: "" });
  const [busy, setBusy] = useState(false);
  const [posterBusy, setPosterBusy] = useState(null);
  const load = () => client.get("/admin/notices").then((r) => setNotices(r.data)).catch(() => {});
  useEffect(() => {
    load();
    client.get("/admin/members").then((r) => setMembers(r.data.filter((m) => m.status === "published"))).catch(() => {});
  }, []);

  const onMember = (id) => {
    const m = members.find((x) => x.id === id);
    setForm({ ...form, member_id: id, member_name: m ? m.business_name : "", borough: m ? m.borough : form.borough });
  };

  const create = async () => {
    if (!form.title.trim()) { toast.error("Add a title."); return; }
    setBusy(true);
    try { await client.post("/admin/notices", { ...form, status: "published" }); toast.success("Notice pinned."); setForm({ title: "", body: "", type: "offer", member_id: "", member_name: "", borough: "" }); await load(); }
    catch { toast.error("Failed."); }
    setBusy(false);
  };
  const genPoster = async (n) => {
    setPosterBusy(n.id);
    try { await client.post(`/admin/notices/${n.id}/generate-poster`); toast.success("Poster generated."); await load(); }
    catch { toast.error("Poster generation failed."); }
    setPosterBusy(null);
  };
  const remove = async (n) => { await client.delete(`/admin/notices/${n.id}`); toast.success("Removed."); load(); };

  return (
    <div>
      <Header kicker="The Noticeboard" title="Offers & Events" />
      <div className="p-6 sm:p-10">
        <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="notice-create-panel">
          <div className="relative z-10 space-y-4">
            <p className="overline text-gloss-gold">Pin a new notice</p>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title e.g. 20% off this month"
              className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold transition-colors" data-testid="notice-title" />
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Details…" rows={2}
              className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold transition-colors resize-none" data-testid="notice-body" />
            <div className="flex flex-col sm:flex-row gap-4">
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="notice-type">
                <option value="offer">offer</option><option value="event">event</option>
              </select>
              <select value={form.member_id} onChange={(e) => onMember(e.target.value)} className="flex-1 bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="notice-member-select">
                <option value="">— Attach a member (adds their logo) —</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.business_name}</option>)}
              </select>
              <input value={form.borough} onChange={(e) => setForm({ ...form, borough: e.target.value })} placeholder="Borough" className="flex-1 bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="notice-borough" />
              <button onClick={create} disabled={busy} className="bg-cream text-ink px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid="notice-create">
                {busy ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />} Pin
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="notices-grid">
          {notices.map((n) => (
            <div key={n.id} className="border border-ink/15 overflow-hidden flex flex-col">
              {assetUrl(n.poster_image) && (
                <div className="relative aspect-[3/2] overflow-hidden bg-ink">
                  <img src={assetUrl(n.poster_image)} alt={n.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-3">
                  <span className="overline text-gloss-silver">{n.type}</span>
                  <div className="flex items-center gap-2">
                    {assetUrl(n.logo) && <img src={assetUrl(n.logo)} alt="" className="h-5 w-auto object-contain" />}
                    <button onClick={() => remove(n)} className="p-1.5 hover:text-red-500 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
                  </div>
                </div>
                <h3 className="font-serif text-xl tracking-tight">{n.title}</h3>
                <p className="font-sans text-sm text-ink/70 mt-2 flex-1">{n.body}</p>
                <p className="font-mono text-xs text-ink/50 mt-3">{n.member_name}{n.borough ? ` · ${n.borough}` : ""}</p>
                <button onClick={() => genPoster(n)} disabled={posterBusy === n.id}
                  className="mt-4 border border-ink/30 px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60"
                  data-testid={`notice-generate-poster-${n.id}`}>
                  {posterBusy === n.id ? <RefreshCw size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                  {n.poster_image ? "Regenerate poster" : "Generate AI poster"}
                </button>
              </div>
            </div>
          ))}
          {notices.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50 lg:col-span-3">No notices yet.</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Partners ---------------- */
